import { type FormatContext, panelContext } from "./context";

/**
 * DATES, HOURS, NUMBERS AND MONEY — one place, and `Intl` every time.
 *
 * ── WHY THIS FILE HAD TO EXIST ────────────────────────────────────────────
 *
 * There were four of these: `features/billing/api/money.ts`,
 * `features/partner/components/money.ts`, `features/reports/format.ts` and
 * `features/inbox/api/format.ts`. Three of them hardcoded `"pt-BR"` and two
 * carried a hand-written list of month names. A panel that can be read in two
 * languages cannot have four opinions about how a number looks, and a
 * hand-written "março" is a translation nobody maintains — `Intl` already knows
 * the month names, the plural rules and the word order of every locale we will
 * ever ship.
 *
 * `.toFixed(2)` appears nowhere on purpose. It is how a total on one screen
 * stops matching the same total on another.
 *
 * ── ABSENCE IS NOT ZERO ───────────────────────────────────────────────────
 *
 * Every formatter answers a missing value with `ABSENT`, never with `0` or
 * `R$ 0,00`. A zero is a fact; a blank is a different fact, and a bill whose
 * total nobody can account for is worse than no bill at all.
 *
 * ── THE CONTEXT IS OPTIONAL, AND DEFAULTS TO THE PANEL'S ──────────────────
 *
 * Called with nothing, these format for the OPERATOR reading the panel. A
 * surface a CUSTOMER reads passes `tenantContext()` explicitly — the argument is
 * there so that choice is visible in the call.
 */

/** What a missing number looks like. One em dash, everywhere. */
export const ABSENT = "—";

/**
 * `Intl` constructors are expensive and these are called per table cell.
 *
 * Keyed by locale plus the options, so a switch of panel language builds new
 * ones instead of handing back the old language's.
 */
const cache = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat>();

function numberFormat(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
	const key = `n|${locale}|${JSON.stringify(options)}`;
	const hit = cache.get(key);
	if (hit) return hit as Intl.NumberFormat;
	const made = new Intl.NumberFormat(locale, options);
	cache.set(key, made);
	return made;
}

function dateFormat(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
	const key = `d|${locale}|${JSON.stringify(options)}`;
	const hit = cache.get(key);
	if (hit) return hit as Intl.DateTimeFormat;
	const made = new Intl.DateTimeFormat(locale, options);
	cache.set(key, made);
	return made;
}

function finite(value: number | null | undefined): value is number {
	return value !== null && value !== undefined && Number.isFinite(value);
}

/** An ISO string as a Date, or null if it is not one. A bad timestamp draws `ABSENT`, never `Invalid Date`. */
function instant(iso: string | Date | null | undefined): Date | null {
	if (!iso) return null;
	const at = iso instanceof Date ? iso : new Date(iso);
	return Number.isNaN(at.getTime()) ? null : at;
}

export function formatNumber(
	value: number | null | undefined,
	ctx: FormatContext = panelContext(),
	options: Intl.NumberFormatOptions = { maximumFractionDigits: 2 },
): string {
	if (!finite(value)) return ABSENT;
	return numberFormat(ctx.locale, options).format(value);
}

/** A count of things. Never a price, never a fraction. */
export function formatCount(value: number | null | undefined, ctx?: FormatContext): string {
	return formatNumber(value, ctx, { maximumFractionDigits: 0 });
}

/**
 * Integer cents to money.
 *
 * Everything the server sends is an integer of the currency's sub-unit, and the
 * only division by a hundred in the panel happens here. The currency may be
 * overridden because it travels beside the amount in every payload: an invoice
 * closed in March keeps the currency it was closed in, and formatting it in
 * today's would relabel history.
 */
export function formatCents(
	cents: number | null | undefined,
	currency?: string,
	ctx: FormatContext = panelContext(),
): string {
	if (!finite(cents)) return ABSENT;
	return formatMoney(cents / 100, currency, ctx);
}

/**
 * Money that is ALREADY in whole units — what a report query returns, and what a
 * price typed into a box means before it becomes cents.
 *
 * Separate from `formatCents` and not a flag on it, because the difference
 * between the two is a factor of a hundred and a boolean argument is not enough
 * warning for that.
 */
export function formatMoney(
	amount: number | null | undefined,
	currency?: string,
	ctx: FormatContext = panelContext(),
): string {
	if (!finite(amount)) return ABSENT;
	return numberFormat(ctx.locale, {
		style: "currency",
		currency: currency || ctx.currency,
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(amount);
}

/** The same number without the symbol, for a column whose header already says the currency. */
export function formatCentsPlain(cents: number | null | undefined, ctx?: FormatContext): string {
	if (!finite(cents)) return ABSENT;
	return formatNumber(cents / 100, ctx, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** A percentage from a 0–100 number, which is what a report query writes. */
export function formatPercent(
	value: number | null | undefined,
	ctx?: FormatContext,
	fractionDigits = 1,
): string {
	if (!finite(value)) return ABSENT;
	return `${formatNumber(value, ctx, { maximumFractionDigits: fractionDigits })}%`;
}

/** Basis points as a percentage: `1000` is `10%`. */
export function formatBasisPoints(bp: number | null | undefined, ctx?: FormatContext): string {
	if (!finite(bp)) return ABSENT;
	return formatPercent(bp / 100, ctx);
}

export function formatDate(
	iso: string | Date | null | undefined,
	ctx: FormatContext = panelContext(),
	dateStyle: Intl.DateTimeFormatOptions["dateStyle"] = "short",
): string {
	const at = instant(iso);
	if (!at) return ABSENT;
	return dateFormat(ctx.locale, { dateStyle, timeZone: ctx.timeZone }).format(at);
}

/**
 * Dias de CALENDÁRIO de hoje até o dia de `iso`, no fuso do workspace — o mesmo dia
 * que `formatDate` escreve. 0 é hoje, negativo já passou. A conta em horas,
 * arredondada para cima, dizia "fim 25/10 · faltam 7 dias" no dia 19 (QA 16).
 */
export function daysUntil(
	iso: string,
	ctx: FormatContext = panelContext(),
	now = new Date(),
): number {
	// `en-CA` escreve AAAA-MM-DD: o dia no fuso, lido de volta como meia-noite UTC.
	const day = (at: Date) => Date.parse(dateFormat("en-CA", { timeZone: ctx.timeZone }).format(at));
	return Math.round((day(new Date(iso)) - day(now)) / 86_400_000);
}

export function formatDateTime(
	iso: string | Date | null | undefined,
	ctx: FormatContext = panelContext(),
): string {
	const at = instant(iso);
	if (!at) return ABSENT;
	return dateFormat(ctx.locale, {
		dateStyle: "short",
		timeStyle: "short",
		timeZone: ctx.timeZone,
	}).format(at);
}

/** Just the clock. In the WORKSPACE's zone: an appointment at 15:45 is not 12:45 for a colleague abroad. */
export function formatTime(
	iso: string | Date | null | undefined,
	ctx: FormatContext = panelContext(),
): string {
	const at = instant(iso);
	if (!at) return ABSENT;
	return dateFormat(ctx.locale, { timeStyle: "short", timeZone: ctx.timeZone }).format(at);
}

/**
 * `2026-03` in words — "March 2026", "março de 2026".
 *
 * The month arrives as `YYYY-MM` and is a string on purpose: it names a billing
 * cycle, not an instant, so it is built at midday UTC where no timezone can move
 * it into the month before.
 */
export function formatMonth(
	referenceMonth: string | null | undefined,
	ctx: FormatContext = panelContext(),
): string {
	if (!referenceMonth) return ABSENT;
	const [year, month] = referenceMonth.split("-");
	const index = Number(month);
	if (!year || !index || index < 1 || index > 12) return referenceMonth;
	const at = new Date(Date.UTC(Number(year), index - 1, 1, 12));
	return dateFormat(ctx.locale, { month: "long", year: "numeric", timeZone: "UTC" }).format(at);
}

/**
 * How long ago, in words — "3 hours ago", "há 3 horas", "yesterday", "ontem".
 *
 * `Intl.RelativeTimeFormat` and not a dictionary of our own: it already has both
 * languages, the plural rules and the special words for ±1 day, and a
 * hand-written version would be a worse translation that we would have to
 * maintain. `numeric: "auto"` is what turns "1 day ago" into "yesterday".
 */
export function formatRelative(
	iso: string | Date | null | undefined,
	ctx: FormatContext = panelContext(),
	now: Date = new Date(),
): string {
	const at = instant(iso);
	if (!at) return ABSENT;
	const seconds = Math.round((at.getTime() - now.getTime()) / 1000);
	const rtf = new Intl.RelativeTimeFormat(ctx.locale, { numeric: "auto" });
	const steps: ReadonlyArray<[Intl.RelativeTimeFormatUnit, number]> = [
		["second", 60],
		["minute", 60],
		["hour", 24],
		["day", 7],
		["week", 4.35],
		["month", 12],
	];
	let value = seconds;
	for (const [unit, size] of steps) {
		if (Math.abs(value) < size) return rtf.format(Math.trunc(value), unit);
		value = value / size;
	}
	return rtf.format(Math.trunc(value), "year");
}

/**
 * How long ago something that ALREADY HAPPENED happened — never in the future.
 *
 * The instant is stamped by the SERVER's `now()`; a browser clock a few seconds
 * behind reads a run that just failed as "in 22 seconds", and "Falhou em 22
 * segundos" is a failure that has not happened yet. The board clamps its
 * "days here" at zero for the same reason (`KanbanCard`). A future instant is
 * read as now. For things that are still to come, `formatRelative`.
 */
export function formatAgo(
	iso: string | Date | null | undefined,
	ctx: FormatContext = panelContext(),
	now: Date = new Date(),
): string {
	const at = instant(iso);
	if (!at) return ABSENT;
	return formatRelative(at.getTime() > now.getTime() ? now : at, ctx, now);
}

/** The largest unit a duration reaches — a three-minute run reads "3 min", not "180 s". */
const DURATION_UNITS = [
	[86_400_000, "day"],
	[3_600_000, "hour"],
	[60_000, "minute"],
] as const;

/** A duration in milliseconds, as a person reads it. Under a second stays in milliseconds. */
export function formatDuration(ms: number | null | undefined, ctx?: FormatContext): string {
	if (!finite(ms)) return ABSENT;
	if (Math.abs(ms) < 1000) return `${formatNumber(Math.round(ms), ctx, {})} ms`;
	const [size, unit] = DURATION_UNITS.find(([size]) => Math.abs(ms) >= size) ?? [1000, "second"];
	return formatNumber(ms / size, ctx, {
		style: "unit",
		unit,
		unitDisplay: "short",
		maximumFractionDigits: 1,
	});
}
